import {
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  updateEmail,
  type User,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { analyticsClient } from '@/lib/analytics';
import {
  computeZodiac,
  isAuthError,
  mapChangeEmailError,
  mapForgotPasswordError,
} from '@/lib/auth';
import { auth, db } from '@/lib/firebase';
import {
  claimInviteCode,
  notificationService,
  setUserInviteCode,
  userService,
  verifyInviteCode,
} from '@/services';

export const authService = {
  onAuthStateChanged(callback: (user: User | null) => void) {
    return onAuthStateChanged(auth, callback);
  },

  async signOut() {
    const currentUser = auth.currentUser;
    if (currentUser) {
      try {
        await notificationService.unregisterMessaging(currentUser.uid);
      } catch (err) {
        console.warn('Failed to deregister notifications on sign out:', err);
      }
    }
    await firebaseSignOut(auth);
  },

  getCurrentUser(): User | null {
    return auth.currentUser;
  },

  async updateLastLogin(uid: string) {
    try {
      const now = new Date().toISOString();
      await userService.updateProfile(uid, { lastLoggedInAt: now, lastActiveAt: now });
    } catch (err) {
      console.error('Failed to update last login time:', err);
    }
  },

  async loginWithEmailPassword(data: { email: string; password: string }) {
    const userCredential = await signInWithEmailAndPassword(auth, data.email, data.password);
    const user = userCredential.user;

    // Check if user document exists
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      await firebaseSignOut(auth);
      throw new Error('AUTH_REQUIRES_INVITE_CODE');
    }

    const userData = userSnap.data() as { status?: string };
    if (userData.status === 'banned') {
      await firebaseSignOut(auth);
      throw new Error('AUTH_BANNED');
    }

    // Generate invite code if missing
    const inviteMappingRef = doc(db, 'userInviteCodes', user.uid);
    const inviteMappingSnap = await getDoc(inviteMappingRef);
    if (!inviteMappingSnap.exists()) {
      void setUserInviteCode().catch(err =>
        console.warn('Failed to create invite code mapping:', err)
      );
    }

    await this.updateLastLogin(user.uid);

    return user;
  },

  async signupWithEmailPassword(data: {
    email: string;
    password: string;
    name: string;
    birthday?: string;
  }) {
    // Create Firebase user
    const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password);
    const user = userCredential.user;

    // Send Verification Email
    try {
      await sendEmailVerification(user);
    } catch (err) {
      analyticsClient.capture('verification_email_send_failed', {
        code: isAuthError(err) ? err.code : 'unknown',
        session_id: analyticsClient.getCurrentSessionId(),
      });
      console.warn('Mail dispatch skipped/failed:', err);
    }

    // Create Draft User Document in Firestore
    const loginTime = new Date().toISOString();
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      name: data.name,
      email: data.email,
      onboarded: false,
      status: 'inactive',
      ...(data.birthday ? { birthday: data.birthday, zodiac: computeZodiac(data.birthday) } : {}),
      createdAt: loginTime,
      lastLoggedInAt: loginTime,
      lastActiveAt: loginTime,
    });

    // Create the invite-code mapping server-side
    void setUserInviteCode().catch(err =>
      console.warn('Failed to create invite code mapping:', err)
    );

    return user;
  },

  async authWithGoogle(mode: 'login' | 'signup') {
    const { GoogleAuthProvider, signInWithPopup } = await import('firebase/auth');
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    const user = userCredential.user;

    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    const loginTime = new Date().toISOString();
    if (!userSnap.exists()) {
      // User doesn't exist - only allow if in signup mode
      if (mode === 'login') {
        await firebaseSignOut(auth);
        throw new Error('AUTH_REQUIRES_INVITE_CODE');
      }
      // Create new user for signup flow
      const userName = user.displayName || user.email?.split('@')[0] || 'Anonymous User';
      await setDoc(userRef, {
        uid: user.uid,
        name: userName,
        email: user.email || '',
        onboarded: false,
        status: 'inactive',
        createdAt: loginTime,
        lastLoggedInAt: loginTime,
        lastActiveAt: loginTime,
      });
      void setUserInviteCode().catch(err =>
        console.warn('Failed to create invite code mapping:', err)
      );
      return { user, isNewUser: true };
    } else {
      const userData = userSnap.data() as { status?: string };
      if (userData.status === 'banned') {
        await firebaseSignOut(auth);
        throw new Error('AUTH_BANNED');
      }

      // User exists - check if they have used an invite code
      const inviteMappingRef = doc(db, 'userInviteCodes', user.uid);
      const inviteMappingSnap = await getDoc(inviteMappingRef);
      if (!inviteMappingSnap.exists()) {
        void setUserInviteCode().catch(err =>
          console.warn('Failed to create invite code mapping:', err)
        );
      }
      await this.updateLastLogin(user.uid);

      return { user, isNewUser: false };
    }
  },

  async updateBirthday(uid: string, birthday: string) {
    await userService.updateProfile(uid, { birthday, zodiac: computeZodiac(birthday) });
  },

  async checkEmailVerificationStatus() {
    const currentUser = auth.currentUser;
    if (currentUser) {
      await currentUser.reload();
      if (currentUser.emailVerified) {
        await this.updateLastLogin(currentUser.uid);
        return true;
      }
    }
    return false;
  },

  async resendVerificationEmail() {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No signed-in user to verify.');
    }
    await sendEmailVerification(currentUser);
  },

  /** Validate, claim, and record the invite code on the authenticated user's profile. */
  async claimInviteCodeForUser(uid: string, code: string): Promise<{ type: 'general' | 'user' }> {
    const trimmedCode = code.trim().toUpperCase();
    const isValid = await verifyInviteCode(trimmedCode);
    if (!isValid) {
      throw new Error('INVALID_INVITE_CODE');
    }
    const { type } = await claimInviteCode(trimmedCode);
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      inviteCodeUsed: trimmedCode,
      inviteCodeUsedAt: new Date().toISOString(),
    });
    return { type };
  },

  async forgotPassword(email: string) {
    const trimmed = email.trim();

    try {
      await sendPasswordResetEmail(auth, trimmed);
    } catch (err) {
      if (isAuthError(err) && err.code === 'auth/user-not-found') {
        // Treat as success to prevent account enumeration.
        return;
      }
      throw new Error(mapForgotPasswordError(err), { cause: err });
    }
  },

  async changeEmail(newEmail: string) {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('changeEmail.errors.saveFailed');
    }

    const trimmed = newEmail.trim();
    try {
      // TODO: prod hardening — verifyBeforeUpdateEmail + reauth for stale sessions
      await updateEmail(user, trimmed);
      await userService.updateProfile(user.uid, { email: trimmed });
    } catch (err) {
      throw new Error(mapChangeEmailError(err), { cause: err });
    }
  },
};
